import React, { useMemo, useState, useRef } from 'react';
import Button, {
  CancelButton,
  CloseButton,
  DeleteButton,
  EnableButton,
  DisableButton,
  SaveButton,
  SaveAndCloseButton,
} from './buttons';
import { Popup } from '@progress/kendo-react-popup';
import LoaderSpinner from 'shared/indicators/LoaderSpinner';
import { useToast } from 'shared/toast';
import { BaseHotKeys } from './inputs/BaseHotKeys';

// eslint-disable-next-line no-unused-vars
const { FormActionBarProps } = require('./forms/typedefs');

const anchorAlign = { horizontal: 'left', vertical: 'top' };
const popupAlign = { horizontal: 'right', vertical: 'bottom' };

/**
 * A footer with actions buttons and hot keys. It is used on the form pages to support various form's actions and to display its submitting state.
 * @component
 * @param {FormActionBarProps} props Component props. Extends BaseFormRenderProps.
 * @returns {React.ReactElement} Footer with actions buttons component.
 */
const FormActionBar = (props) => {
  const {
    // Actions
    hideCloseButton = false,
    onClose,
    // Delete
    onAsyncDelete,
    onDelete,
    showDeleteButton = false,
    // Edit Mode
    onEnableChange,
    showEnableStateButtons = false,
    // Copy Form Item
    allowCopy = false,
    onCopy,
    // Submit
    allowSubmit = false,
    alwaysEnableSaveButton = false,
    hideChangesSavedMessage = false,
    hideSave = false,
    hideSaveAndClose = false,
    isSubmitting = false,
    onSubmit,
    saveTitle = 'Save',
    submitted = false,
    // Form props
    isDirty,
    modified = false,
    onChange,
    errors,
    // Rest
    customActions,
    customRender,
    isEnabled,
    isNew,
    itemName,
    readOnly = false,
    enableHotkeys = true,
    customDeferredAction,
    saveDisabled
  } = props;

  const toast = useToast();
  
  // Small screen dropdown
  const [showPopup, setShowPopup] = useState(false);
  const anchor = useRef(null);

  // Save click handlers
  const [closeOnSubmit, setCloseOnSubmit] = useState(false);

  // Show validation summary
  const handleValidationError = () => {
    if (errors && errors.VALIDATION_SUMMARY) {
      toast.error({ message: errors.VALIDATION_SUMMARY });
    }
  };

  const onSaveClick = () => {
    handleValidationError();
    setCloseOnSubmit(false);
    if (onChange) {
      onChange('closeOnSubmit', { value: false });
    }
    if (onSubmit) {
      onSubmit({ closeOnSubmit: false });
    }
    setShowPopup(false);
  };

  const onSaveAndCloseClick = () => {
    handleValidationError();
    setCloseOnSubmit(true);
    if (onChange) {
      onChange('closeOnSubmit', { value: true });
    }
    if (onSubmit) {
      onSubmit({ closeOnSubmit: true });
    }
    setShowPopup(false);
  };

  const onSaveAndCopyClick = () => {
    handleValidationError();
    onChange('copyOnSubmit', { value: true });
    setCloseOnSubmit(true);
  };

  const EnableStateChangeButton = useMemo(
    () => (isEnabled ? DisableButton : EnableButton),
    [isEnabled],
  );

  const dataWasSaved = submitted && !modified;
  const saveIsDisabled = isSubmitting || ((!allowSubmit || dataWasSaved) && !alwaysEnableSaveButton) || saveDisabled;
  const closeProps = { disabled: isSubmitting, onClick: onClose };

  // Content
  const content = customRender || (
    <>
      <div className='form-action-bar-left'>
        <div className='loading-status'>
          {isSubmitting && <LoaderSpinner size='small' />}
          {(dataWasSaved && !hideChangesSavedMessage) && <div className='feedback-success'>Changes Saved</div>}
        </div>
        <div className='action-bar-main-buttons'>
          {(!hideSave && !readOnly) && (
            <SaveButton
              type={onSubmit ? 'button' : 'submit'}
              onClick={onSaveClick}
              disabled={saveIsDisabled}
              title={`${saveTitle} [s]`}
              isLoading={isSubmitting && !closeOnSubmit}
              responsive={false}
            />
          )}
          {(!hideSaveAndClose && !readOnly) && (
            <SaveAndCloseButton
              onClick={onSaveAndCloseClick}
              disabled={saveIsDisabled}
              title={`${saveTitle} & Close [q]`}
              isLoading={isSubmitting && closeOnSubmit}
            />
          )}
          {!hideCloseButton && ((allowSubmit && !readOnly)
            ? <CancelButton {...closeProps} title={`${closeProps?.title || 'Cancel'} [c]`} />
            : <CloseButton {...closeProps} title={`${closeProps?.title || 'Close'} [c]`} />)}
          {customActions}
          {customDeferredAction}
        </div>
      </div>
      <div className="form-action-bar-right">
        {showEnableStateButtons && !readOnly && onEnableChange && (
          <EnableStateChangeButton
            disabled={isSubmitting}
            onClick={onEnableChange}
          />
        )}
        {allowCopy && !readOnly && (!isNew || isDirty) && (
          <Button
            id='save-and-copy-button'
            type={isDirty ? 'submit' : 'button'}
            disabled={isSubmitting}
            title={isDirty ? 'Save & Copy' : 'Copy'}
            icon='copy'
            onClick={isDirty ? onSaveAndCopyClick : onCopy}
          />
        )}
        {showDeleteButton && !readOnly && (onDelete || onAsyncDelete) && (
          <DeleteButton
            disabled={isSubmitting}
            title="Delete [d]"
            onClick={() => {
              setShowPopup(false);
              if (onDelete) {
                onDelete();
              } else if (onAsyncDelete) {
                onAsyncDelete();
              }
            }}
          />
        )}
      </div>
    </>
  );

  const handleHotKeySave = () => {
    if (enableHotkeys && !hideSave && !readOnly && !isSubmitting && !saveIsDisabled) {
      onSaveClick?.();
    }
  };

  const handleHotKeySaveCloseClick = () => {
    if (enableHotkeys && !hideSaveAndClose && !readOnly && !isSubmitting && !saveIsDisabled) {
      onSaveAndCloseClick?.();
    }
  };

  const handleHotKeyClose = () => {
    if (enableHotkeys && !hideCloseButton && !readOnly && !isSubmitting) {
      onClose?.();
    }
  };

  const handleHotKeyDelete = () => {
    if (showDeleteButton && !readOnly && (onDelete || onAsyncDelete)) {
      setShowPopup(false);
      if (onDelete) {
        onDelete();
      } else if (onAsyncDelete) {
        onAsyncDelete();
      }
    }
  };

  const hotKeyMap = {
    SAVE: 'alt+s',
    SAVE_CLOSE: 'alt+q',
    CLOSE: 'alt+c',
    DELETE: 'alt+d',
  };

  const hotKeyHandlers = {
    SAVE: handleHotKeySave,
    SAVE_CLOSE: handleHotKeySaveCloseClick,
    CLOSE: handleHotKeyClose,
    DELETE: handleHotKeyDelete,
  };

  return (
    <BaseHotKeys
      keyMap={hotKeyMap}
      handlers={hotKeyHandlers}
    >
      {/* Small screen */}
      <div className='form-action-bar small-screen-bar'>
        {(!hideSave && !readOnly) && (
          <SaveButton
            type={onSubmit ? 'button' : 'submit'}
            onClick={onSaveClick}
            disabled={saveIsDisabled}
            title={`${saveTitle} [s]`}
            isLoading={isSubmitting && !closeOnSubmit}
            responsive={false}
          />
        )}
        {!hideCloseButton && ((allowSubmit && !readOnly)
          ? (
            <CancelButton
              {...closeProps}
              title={`${closeProps?.title || 'Cancel'} [c]`}
            />
          ) : (
            <CloseButton
              {...closeProps}
              title={`${closeProps?.title || 'Close'} [c]`}
            />
          ))}
        <div ref={anchor} className='form-action-bar-popup-button'>
          <Button
            icon='menu'
            onClick={() => setShowPopup((prev) => !prev)}
          />
        </div>
        <Popup
          anchor={anchor.current}
          show={showPopup}
          className='form-action-bar-popup'
          anchorAlign={anchorAlign}
          popupAlign={popupAlign}
        >
          {content}
        </Popup>
      </div>
      <div className='form-action-bar normal-screen-bar'>{content}</div>
    </BaseHotKeys>
  );
};

export default FormActionBar;
